import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, Button, SliderProps, useTheme } from '@mui/material';

import debounce from 'lodash/debounce';

import { scaledRangeChartValues } from '@arrived/common';
import { useFormatPercent } from '@arrived/hooks';

import { Slider, RangeBlock } from '../../../Slider';
import { CalculatorCardStat } from './ROICalculatorPage/CalculatorCardStat';
import { ROICard } from './ROICard';

import { ArrivedTooltip } from '../../../tooltips';
import { StyledControlContainer, StyledSliderContainer } from './components';

const MIN_SLIDER_VALUE = -0.25;
const MAX_SLIDER_VALUE = 0.25;
const bounds = { min: MIN_SLIDER_VALUE, max: MAX_SLIDER_VALUE };
interface AppreciationGrowthCardProps {
  appreciation: {
    lower: number;
    upper: number;
  };
  appreciationRate: number;
  setAppreciationRate: (appreciationRate: number) => void;
  debtLeverage: number;
  equityGrowthRate: number;
  showCharts: boolean;
  marketName: string;
  toggleShowCharts: () => void;
}

export const AppreciationGrowthCard = ({
  appreciation,
  debtLeverage,
  equityGrowthRate,
  showCharts,
  marketName,
  toggleShowCharts,
  setAppreciationRate,
  appreciationRate,
}: AppreciationGrowthCardProps) => {
  const { palette } = useTheme();
  const debouncedSetAppreciationRate: SliderProps['onChange'] = useMemo(() => {
    return debounce((_, val) => {
      const newAppreciationRate = Number(val);
      setAppreciationRate(newAppreciationRate);
    }, 10);
  }, [setAppreciationRate]);

  const formatPercent = useFormatPercent();
  return (
    <ROICard
      color={palette.tertiary[500]}
      titleNumber={1}
      title={<FormattedMessage id="calculator-page-property-value-growth" defaultMessage="Property Value Growth" />}
    >
      <Box
        display="flex"
        width="100%"
        gap={1}
        flexDirection="column"
        mt={2}
        alignItems="center"
        height="250px"
        textAlign="center"
      >
        <CalculatorCardStat
          label={
            <FormattedMessage id="calculator-property-appreciation-title" defaultMessage="Property Appreciation" />
          }
          percentage={appreciationRate}
          PercentTypographyProps={{ color: ({ palette }) => palette.darkened.steel }}
        />
        <CalculatorCardStat
          label={
            <ArrivedTooltip
              title={
                <FormattedMessage
                  id="calculator-property-equity-returns-title"
                  defaultMessage="Property Equity Returns"
                />
              }
              titleTypography={{
                variant: 'body1.semibold',
              }}
            >
              <FormattedMessage
                id="calculator-property-appreciation-tooltip"
                defaultMessage="This is the estimated growth of investor share value after 1 year at the corresponding property appreciation selected. The calculation takes into account the prorated real estate investment costs (Closing Costs, Offering Costs, Holding Costs & the Arrived Sourcing Fee). The calculation factors in leverage of {leverage} on this property. For short term rentals, Property Improvements & Cash Reserves are excluded from this calculation."
                values={{
                  leverage: formatPercent(debtLeverage),
                }}
              />
            </ArrivedTooltip>
          }
          percentage={equityGrowthRate}
          PercentTypographyProps={{
            color: ({ palette }) => palette.tertiary[500],
            fontWeight: 'semibold',
            variant: 'h5',
          }}
        />
        <StyledControlContainer>
          <StyledSliderContainer>
            {appreciation.lower && appreciation.upper && (
              <RangeBlock
                label={
                  <ArrivedTooltip
                    disableUnderline
                    titleVariant="body2"
                    title={<FormattedMessage id="historical-average" defaultMessage="Historical Average" />}
                  >
                    <FormattedMessage
                      id="appreciation-historical-average-tooltip"
                      defaultMessage={`This is the estimated historical average annual property appreciation in the
                      {marketName} market for the last 10 & 20 years. The data is provided by independent data providers,
                      including HouseCanary or Zillow.
                   `}
                      values={{
                        br: <br />,
                        marketName,
                      }}
                    />
                  </ArrivedTooltip>
                }
                leftLabel={formatPercent(appreciation.lower)}
                rightLabel={formatPercent(appreciation.upper)}
                $start={scaledRangeChartValues(appreciation.lower, bounds)}
                $width={
                  scaledRangeChartValues(appreciation.upper, bounds) -
                  scaledRangeChartValues(appreciation.lower, bounds)
                }
              />
            )}
            <Slider
              key={`slider-${appreciation.lower}-${appreciation.upper}`}
              className="card-slider"
              $color={palette.tertiary[500]}
              min={MIN_SLIDER_VALUE}
              max={MAX_SLIDER_VALUE}
              step={0.0005}
              value={appreciationRate}
              defaultValue={appreciation.lower}
              onChange={debouncedSetAppreciationRate}
            />
          </StyledSliderContainer>
        </StyledControlContainer>
      </Box>
      <Button variant="text" size="small" className="calculator-view-charts-button" onClick={toggleShowCharts}>
        {showCharts ? (
          <FormattedMessage id="calculator-view-charts-hide" defaultMessage="Hide Charts" />
        ) : (
          <FormattedMessage id="calculator-view-charts-show" defaultMessage="View Charts" />
        )}
      </Button>
    </ROICard>
  );
};
